import React, { useState, useEffect } from 'react';
import '../styles/Sidebar.css';

function hasFeat(features, key) {
  if (!features) return false;
  const v = features[key];
  if (v == null) return false;
  if (typeof v === 'boolean') return v;
  return v.enabled === true;
}

const getNav = (userRole, inAuditView, customerVisiblePages) => {
  const isCA = userRole?.isCA || false;
  const viewMode = userRole?.viewMode || 'ca';
  const isAdmin = (userRole?.isAdmin || false) && viewMode === 'admin';
  const isCustomer = userRole?.isCustomer || false;
  const isTeamMember = userRole?.isTeamMember || false;
  const perms = userRole?.teamMembership;

  const features = userRole?.subscriptionFeatures || null;

  const featEnabled = (key) => {
    if (isAdmin) return true;
    if (features === null) {
      const defaults = {
        tally_sync: true,
        ai_reports: true,
        report_download: false,
        gst_filing: false,
        team_members: false,
        reconciliation: false,
        confidence_score: false,
        ai_chatbot: false,
        client_portal: false,
      };
      return defaults[key] ?? false;
    }
    return hasFeat(features, key);
  };

  const canViewData = !perms || perms.can_view_data;
  const canViewReports = !perms || perms.can_view_reports;
  const canRegister = !perms || perms.can_register;
  const canManageHSN = !perms || perms.can_manage_hsn;
  const accountCanSync = userRole?.canSync ?? (isCA || isAdmin);
  const canRunSync = accountCanSync && (!perms || perms.can_run_sync);

  const canShow = (id) => {
    if (!isCustomer) return true;
    if (!customerVisiblePages) return true;
    return customerVisiblePages.includes(id);
  };

  const dashboardItem = isCA
    ? { id: 'cadashboard', label: 'Engagements' }
    : { id: 'customerdashboard', label: 'My Requests' };

  return [
    {
      section: 'Overview', items: [
        dashboardItem,
        ...(isCA ? [{ id: 'my_requests', label: 'My Requests' }] : []),
        ...(isAdmin ? [{ id: 'engagement_requests', label: 'Engagement Requests' }] : []),
        ...(isAdmin ? [{ id: 'caqueue',             label: 'CA Requests' }] : []),
        ...(isAdmin ? [{ id: 'empanelled_cas',       label: 'Empanelled CAs' }] : []),
        ...(isAdmin ? [{ id: 'users_admin',          label: 'All Users' }] : []),
        ...(isCA && !isTeamMember && featEnabled('team_members') ? [{ id: 'team', label: 'Team Members' }] : []),
        ...(isCA && !isTeamMember ? [{ id: 'customers', label: 'Customers' }] : []),
      ]
    },

    ...(inAuditView && canViewReports ? [{
      section: 'Audit', items: [
        ...(canShow('overview') ? [{ id: 'overview', label: 'Quick Snapshot' }] : []),
        ...(canShow('report') && featEnabled('ai_reports')
          ? [{ id: 'report', label: 'Audit Report' }]
          : []),
        ...(canShow('audit') && featEnabled('ai_reports')
          ? [{ id: 'audit', label: 'Audit Issues' }]
          : []),
      ].filter(Boolean)
    }] : []),

    ...(inAuditView && canViewData ? [{
      section: 'Compliance', items: [
        ...(canShow('statutory') ? [{ id: 'statutory', label: 'Statutory Check' }] : []),
        ...(canShow('unbooked')  ? [{ id: 'unbooked',  label: 'Unbooked Payments' }] : []),
        ...(canShow('balance')   ? [{ id: 'balance',   label: 'Balance Check' }] : []),
      ].filter(Boolean)
    }] : []),

    ...(inAuditView && canViewData ? [{
      section: 'Financials', items: [
        ...(canShow('receivables') ? [{ id: 'receivables', label: 'Receivables' }] : []),
        ...(canShow('payables')    ? [{ id: 'payables',    label: 'Payables' }] : []),
        ...(canShow('trial')       ? [{ id: 'trial',       label: 'Trial Balance' }] : []),
      ].filter(Boolean)
    }] : []),

    ...(inAuditView && canViewData ? [{
      section: 'Registers', items: [
        ...(canShow('sales')    ? [{ id: 'sales',    label: 'Sales Register' }] : []),
        ...(canShow('purchase') ? [{ id: 'purchase', label: 'Purchase Register' }] : []),
        ...(canShow('gst') && featEnabled('gst_filing')
          ? [{ id: 'gst', label: 'GST Invoices' }]
          : []),
        ...(canShow('bank') ? [{ id: 'bank', label: 'Bank Book' }] : []),
      ].filter(Boolean)
    }] : []),

    {
      section: 'System', items: [
        ...(canManageHSN ? [{ id: 'config', label: 'Configuration' }] : []),
        ...(canRunSync && inAuditView && canShow('sync') ? [{ id: 'sync', label: 'Sync History' }] : []),
        { id: 'devices', label: 'My Devices' },
        ...(isAdmin ? [{ id: 'pricing_admin', label: 'Pricing Config' }] : []),
      ]
    },

    {
      section: 'Account', items: [
        { id: 'profile', label: 'My Profile' },
        ...(!isTeamMember ? [{ id: 'subscription', label: 'Subscription' }] : []),
        ...(isCA ? [{ id: 'ca_reviews', label: 'My Reviews' }] : []),
      ]
    },
  ].filter(group => group.items.length > 0);
};

export default function Sidebar({ current, onNav, userRole, isOpen, onClose, inAuditView, customerVisiblePages }) {
  const NAV = getNav(userRole, inAuditView, customerVisiblePages);
  const handleNav = (id) => { onNav(id); onClose?.(); };

  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    setCollapsed(prev => ({ ...prev, Overview: !!inAuditView }));
  }, [inAuditView]);

  const toggleSection = (section) => {
    setCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sdb-logo">
          <div className="sdb-logo-inner">
            <div className="sdb-logo-icon">CA</div>
            <div>
              <div className="sdb-logo-title">Audit PrOS</div>
              <div className="sdb-logo-sub">{inAuditView ? '● Viewing Audit' : 'Dashboard'}</div>
            </div>
          </div>
          <button onClick={onClose} className="sidebar-close-btn">×</button>
        </div>

        <nav className="sdb-nav">
          {NAV.map(group => {
            const isCollapsed = !!collapsed[group.section];
            return (
              <div key={group.section}>
                <div
                  className={`sdb-section-label sdb-section-label--toggle${isCollapsed ? ' sdb-section-label--collapsed' : ''}`}
                  onClick={() => toggleSection(group.section)}
                >
                  {group.section}
                  <span className="sdb-section-chevron">{isCollapsed ? '›' : '⌄'}</span>
                </div>
                {!isCollapsed && group.items.map(item => {
                  const active = current === item.id;
                  const isCAQueue = item.id === 'caqueue';
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNav(item.id)}
                      className={`sdb-nav-btn${active ? ' sdb-nav-btn--active' : ''}`}
                    >
                      <span className={`sdb-nav-dot${isCAQueue ? ' sdb-nav-dot--caqueue' : active ? ' sdb-nav-dot--active' : ''}`} />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="sdb-footer">v2.0 · Tally + Zoho</div>
      </aside>
    </>
  );
}
